import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { MapPin, Search, Sparkles, X } from "lucide-react";

import { getSuggestions, type SearchableTour, type Suggestion } from "@/lib/tour-search";

interface Props {
  products: readonly SearchableTour[];
  value: string;
  onChange: (v: string) => void;
  onSubmit: (v: string) => void;
  placeholder?: string;
}

/** Autocomplete input that surfaces cities, "Tours en X" phrases and products. */
export function TourAutocomplete({
  products,
  value,
  onChange,
  onSubmit,
  placeholder,
}: Props) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const suggestions = useMemo(
    () => getSuggestions(value, products, 8),
    [value, products],
  );

  // Close on outside click.
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [value]);

  function pick(s: Suggestion) {
    setOpen(false);
    if (s.kind === "city") {
      navigate({ to: "/tours/$city", params: { city: s.slug } });
      return;
    }
    if (s.kind === "phrase") {
      onChange(s.query);
      onSubmit(s.query);
      return;
    }
    // product: just put title in box and run inline search
    onChange(s.label);
    onSubmit(s.label);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown" && suggestions.length) {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (i + 1) % suggestions.length);
      return;
    }
    if (e.key === "ArrowUp" && suggestions.length) {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
      return;
    }
    if (e.key === "Enter") {
      if (open && suggestions[activeIndex]) {
        e.preventDefault();
        pick(suggestions[activeIndex]);
      }
      return;
    }
    if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="flex items-center gap-2">
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder ?? "Cancún, Tulum, cenotes, gastronomía…"}
          className="h-7 w-full border-0 bg-transparent px-0 text-sm font-semibold text-foreground outline-none placeholder:font-medium placeholder:text-muted-foreground/70"
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={open}
          role="combobox"
        />
        {value && (
          <button
            type="button"
            aria-label="Limpiar"
            onClick={() => {
              onChange("");
              inputRef.current?.focus();
            }}
            className="shrink-0 rounded-full p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-50 max-h-80 overflow-y-auto rounded-2xl border border-border bg-popover p-1.5 shadow-[0_24px_60px_-20px_oklch(0_0_0/0.35)]"
        >
          {suggestions.map((s, i) => {
            const active = i === activeIndex;
            const Icon = s.kind === "city" ? MapPin : s.kind === "phrase" ? Search : Sparkles;
            return (
              <li key={`${s.kind}-${i}-${s.label}`} role="option" aria-selected={active}>
                <button
                  type="button"
                  onMouseEnter={() => setActiveIndex(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(s);
                  }}
                  className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition ${
                    active ? "bg-accent text-accent-foreground" : "text-foreground hover:bg-accent/60"
                  }`}
                >
                  <span
                    className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${
                      s.kind === "city" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold">{s.label}</span>
                    <span className="block truncate text-xs text-muted-foreground">{s.sublabel}</span>
                  </span>
                  {s.kind === "city" && s.count > 0 && (
                    <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                      {s.count}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
